'use client';

import { storeProduct, selectCountProductInCart } from '@/fo/store/product';

import { isLandingPath } from '../utils/isLandingPath';

import { sendGTMEvent } from './sendGTMEvent';
import {
  GTMEventAuth,
  GTMEventEcommerce,
  GTMEcommerceItem,
  GTMEventNameEcommerce,
  GTMItemListName
} from './types';

// Хранилище листов для события просмотра перед добавлением в корзину
const storeItemListName = new Map<string, GTMItemListName>();
const viewedEvents = ['view_item', 'view_item_list'];
const FALLBACK_ITEM_LIST_NAME: GTMItemListName = 'cart';
const STORAGE_KEY = 'gtm:item-list-name';
let isStorageHydrated = false;

const hydrateItemListNameStore = () => {
  if (isStorageHydrated || typeof window === 'undefined') {
    return;
  }

  isStorageHydrated = true;

  const value = window.localStorage.getItem(STORAGE_KEY);
  if (!value) {
    return;
  }

  const parsed = JSON.parse(value) as Record<string, GTMItemListName>;
  Object.entries(parsed).forEach(([itemId, itemListName]) => {
    if (itemListName) {
      storeItemListName.set(itemId, itemListName);
    }
  });
};

const syncItemListNameStore = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(Object.fromEntries(storeItemListName))
  );
};

const normalizeItemName = (name: string) =>
  name
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const sendGTMEventEcommerce = <T extends GTMEventNameEcommerce>(
  data: GTMEventEcommerce<T>
) => {
  if (
    data.event === 'add_to_cart' &&
    typeof window !== 'undefined' &&
    isLandingPath(window.location.pathname)
  ) {
    return;
  }

  hydrateItemListNameStore();

  // sendGTMEventEcommerce будет вызван, после обновления корзины
  const snapshot = storeProduct.getSnapshot();
  const isViewedEvent = viewedEvents.includes(data.event);

  const items: GTMEcommerceItem[] = data.ecommerce.items.map((item) => {
    const cartCount = selectCountProductInCart(item.item_id)(snapshot);

    if (isViewedEvent && cartCount === 0) {
      // Сохраняем лист, при просмотре товара, если товара нет в корзине
      storeItemListName.set(
        item.item_id,
        item?.item_list_name || FALLBACK_ITEM_LIST_NAME
      );
      syncItemListNameStore();
    }

    const isRemovedItem = data.event === 'remove_from_cart' && cartCount === 0;
    if (isRemovedItem) {
      // Удаляем лист, при полном удалении товара из корзины
      storeItemListName.delete(item.item_id);
      syncItemListNameStore();
    }

    // Берем лист из хранилища, для всех событий, кроме просмотра товара
    const listName = isViewedEvent
      ? item.item_list_name // В события просмотра всегда передается лист
      : storeItemListName.get(item.item_id) || FALLBACK_ITEM_LIST_NAME;
    return {
      ...item,
      item_name: normalizeItemName(item.item_name),
      item_brand: item.item_brand || null,
      item_category: item.item_category || null,
      variant: item.variant || null,
      item_list_name: listName,
      quantity: item.quantity ?? 1
    };
  });

  const isCartChangeEvent =
    data.event === 'add_to_cart' || data.event === 'remove_from_cart';
  if (isCartChangeEvent && !items.some((item) => (item.quantity ?? 0) > 0)) {
    return;
  }

  sendGTMEvent({ ecommerce: null });
  sendGTMEvent({
    event: data.event,
    ecommerce: {
      currency: data.ecommerce.currency || 'RUB',
      items,
      ...(data.ecommerce.value && { value: data.ecommerce.value }),
      ...(data.ecommerce.id && { id: data.ecommerce.id }),
      ...(data.ecommerce.coupon && { coupon: data.ecommerce.coupon })
    }
  });

  if (data.event === 'purchase') {
    storeItemListName.clear();
    syncItemListNameStore();
  }
};

export const sendGTMEventAuth = (data: GTMEventAuth) => {
  sendGTMEvent(data);
};

export type GTMMaterialType = 'article' | 'news' | 'video';

export function pushClickMaterialGtm(params: {
  eventCategory: string;
  moduleType: GTMMaterialType;
  moduleTitle: string;
}) {
  sendGTMEvent({
    event: 'click_material',
    eventCategory: params.eventCategory,
    eventAction: `select::${params.moduleType}`,
    eventLabel: params.moduleTitle
  });
}

export function pushCheckoutStageGtm(
  eventCategory: 'delivery_method' | 'payment_method',
  eventAction: string
) {
  sendGTMEvent({
    event: 'checkout_stage',
    eventCategory,
    eventAction
  });
}

export { sendGTMEvent };
